import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import MicRecorder from 'mic-recorder-to-mp3'; 
import { pcmEncode, downsampleBuffer } from '../../../../util/audio/awsUtils'; 
import { createPresignedURL } from '../../../../util/audio/awsSignature';
import { fromUtf8, toUtf8 } from "@smithy/util-utf8";
import getUserMedia from 'get-user-media-promise';
import { Buffer } from 'buffer';
import CryptoJS from 'crypto-js';

import { EventStreamCodec } from '@smithy/eventstream-codec'; 
import { default as MicrophoneStream } from 'microphone-stream'; // collect microphone input as a stream of raw bytes



// Components 

import Box from '@mui/material/Box';
import MicNoneIcon from '@mui/icons-material/MicNone';
import PauseIcon from '@mui/icons-material/Pause';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { editConsultationNote, getAutoComplete, transcribeAudio, updateConsultationNote } from '../../../../routes/doctor/consultation';
import { setCurrentAudioFile, setIsListening, setIsRecording, setSelectedNoteField } from '../../../../redux/features/doctor/Dashboard/consultation/noteMicrophone';

// -- 

const Microphone: React.FC = () => {

    const dispatch = useAppDispatch(); 
    const { t } = useTranslation(); 
    const navigate = useNavigate(); 

    const { consultationId, consultationNoteId, securityPin } = useParams(); 

    const accountInfo = useAppSelector(state => state.meta.accountInfo); 

    const colors = useAppSelector(state => state.theme.colors); 
    const consultationInfo = useAppSelector(state => state.noteMicrophone.consultationInfo); 
    const isMicrophoneDisabled = useAppSelector(state => state.noteMicrophone.isMicrophoneDisabled); 
    const isRecording = useAppSelector(state => state.noteMicrophone.isRecording); 
    const language = useAppSelector(state => state.noteMicrophone.language); 
    const mode = useAppSelector(state => state.noteMicrophone.mode); 
    const selectedNoteField = useAppSelector(state => state.noteMicrophone.selectedNoteField); 
    const isListening = useAppSelector(state => state.noteMicrophone.isListening); 

    const [timeLeft, setTimeLeft] = useState(0); 

    // const [isRecording, setIsRecording] = useState<boolean>(false);
    const [recordingTime, setRecordingTime] = useState<number>(0);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [transcribedText, setTranscribedText] = useState(''); 
    const [isConsent, setIsConsent] = useState(true); 

    // -- 

    const handleTranscription = async (blob: Blob) => {

        const formData = new FormData() as any; 
        
        formData.append('file', blob, `recording.webm`);
        formData.append('consultationId', consultationId); 
        formData.append('consultationNoteId', consultationNoteId); 
        formData.append('securityPin', securityPin); 
        formData.append('micType', 'mobile'); 

        try {

            const res = await transcribeAudio(formData) as any; 

            if (res.status == 200) { 

            }; 

        } catch (err: any) {

        } finally {

        }; 

    };

    // -- 

    const startRecording = useCallback(async () => {

        setTimeLeft(2); 

        if (!isConsent || isMicrophoneDisabled) {

            // setIsMicActivatedBeforeConsent(true); 

            // dispatch(setIsConsentPopupOpened(true)); 

            return; 

        };


        // setError(null);
        // dispatch(setSegments([])); 

        try {

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) audioChunksRef.current.push(event.data);
            };

            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: mediaRecorderRef.current?.mimeType || 'audio/webm' });
                handleTranscription(blob); // Process audio
                stream.getTracks().forEach(track => track.stop());
            };
            mediaRecorderRef.current.onerror = (event) => {/*... error handling ...*/ dispatch(setIsRecording(false));};

            mediaRecorderRef.current.start();

            dispatch(setIsRecording(true)); 

            const content = { 
                consultationNoteId,
                securityPin, 
                startRecording: true,
                micType: 'mobile'
            }; 

            updateConsultationNote(content); 

        } catch (err) {
            // ... (error handling as before, including permission denial) ...
        }
    }, [isConsent]); 

    // --  

    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            dispatch(setIsRecording(false)); 
            setTimeLeft(0); 
            // setRecordingTime(0); // Reset timer
            // Note: timer interval cleared by useEffect dependency change

            const content = { 
                consultationNoteId,
                securityPin, 
                stopRecording: true,
            }; 

            updateConsultationNote(content); 
        }
    }, [isRecording]);

    // -- 

    return (

        <>

        { isMicrophoneDisabled ?
            
            <Box
                sx={{ 
                    display: 'flex',
                    flexDirection: 'row', 
                    alignItems: 'center', 
                    justifyContent: 'space-evenly', 
                    borderRadius: '50%', 
                    width: 100, 
                    height: 100,
                    borderStyle: 'solid',
                    borderWidth: 1, 
                    cursor: 'default', 
                    color: 'white', 
                    borderColor: 'rgb(180, 180, 180)',
                    backgroundColor: 'rgb(180, 180, 180)', 
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                }}
            >

                <MicNoneIcon sx={{ fontSize: 40 }} />

            </Box>: 
            
            <Box
                onClick={isRecording ? stopRecording : startRecording}
                    sx={{ 
                        display: 'flex',
                        flexDirection: 'row', 
                        alignItems: 'center', 
                        justifyContent: 'space-evenly', 
                        borderRadius: '50%', 
                        width: 100, 
                        height: 100,
                        borderStyle: 'solid',
                        borderWidth: 1, 
                        cursor: 'pointer',
                        color: 'white', 
                        borderColor: isRecording ? colors?.red : colors?.main,
                        backgroundColor: isRecording ? colors?.red : colors?.main, 
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                        '&:hover': { 
                            color: 'white',
                            backgroundColor: isRecording ? 'rgb(232, 98, 30)' : colors?.main,
                            borderColor: isRecording ? 'rgb(232, 98, 30)' : colors?.main,
                        }
                }}
            >
                <MicNoneIcon sx={{ fontSize: 40 }} />

            </Box>}

    </>

  );

};

export default Microphone;